// 2
// Path: frontend/src/components/QuickBooksSync/quickbooksEntities.js
// Inputs: None (constants and normalization utility functions)
// Outputs: Exported QUICKBOOKS_TARGET_ENTITIES array, helper functions for metadata lookup and entity normalization
// Summary: Single canonical source of truth for all QuickBooks Online target landing entities across connection groups, pipelines, and standard mapping presets.

export const QUICKBOOKS_TARGET_ENTITIES = [
    {
        name: 'Customer',
        entity: 'Customer',
        label: 'Customer (Accounts Receivable)',
        category: 'Sales & AR',
        defaultTable: 'CUSFIL',
        icon: 'person',
        color: 'emerald',
        description: 'Customer master records, billing & shipping addresses, contact details, credit terms'
    },
    {
        name: 'Vendor',
        entity: 'Vendor',
        label: 'Vendor (Accounts Payable)',
        category: 'Expenses & AP',
        defaultTable: 'VENFIL',
        icon: 'storefront',
        color: 'sky',
        description: 'Vendor master records, pay-to addresses, payment terms, tax ID numbers'
    },
    {
        name: 'Item',
        entity: 'Item',
        label: 'Item (Products & Services)',
        category: 'Inventory & Sales',
        defaultTable: 'ITMFIL',
        icon: 'inventory_2',
        color: 'amber',
        description: 'Inventory items, non-inventory parts, service items, unit costs, pricing, income/cogs accounts'
    },
    {
        name: 'Account',
        entity: 'Account',
        label: 'Account (General Ledger)',
        category: 'Accounting & GL',
        defaultTable: 'CHARTF',
        icon: 'account_balance',
        color: 'purple',
        description: 'Chart of accounts, account numbers, account types, sub-types, classifications'
    },
    {
        name: 'Invoice',
        entity: 'Invoice',
        label: 'Invoice (Sales Orders & Billing)',
        category: 'Sales & AR',
        defaultTable: 'OEHDR',
        icon: 'receipt',
        color: 'blue',
        description: 'Customer invoices, sales orders, due dates, billing addresses, line items'
    },
    {
        name: 'Bill',
        entity: 'Bill',
        label: 'Bill (Vendor Invoices & AP)',
        category: 'Expenses & AP',
        defaultTable: 'POHDR',
        icon: 'request_quote',
        color: 'orange',
        description: 'Vendor bills, AP invoices, payment due dates, expense account allocations'
    },
    {
        name: 'Payment',
        entity: 'Payment',
        label: 'Payment (Customer Cash Receipts)',
        category: 'Sales & AR',
        defaultTable: 'ARTRN',
        icon: 'payments',
        color: 'teal',
        description: 'Customer payments, cash receipts, payment methods, bank deposit accounts'
    },
    {
        name: 'JournalEntry',
        entity: 'JournalEntry',
        label: 'Journal Entry (General Journal)',
        category: 'Accounting & GL',
        defaultTable: 'GLTRN',
        icon: 'menu_book',
        color: 'violet',
        description: 'General ledger journal vouchers, debits, credits, account adjustments'
    },
    {
        name: 'PurchaseOrder',
        entity: 'PurchaseOrder',
        label: 'Purchase Order',
        category: 'Purchasing',
        defaultTable: 'POHDR',
        icon: 'shopping_cart',
        color: 'indigo',
        description: 'Vendor purchase orders, purchasing lines, delivery dates, shipping instructions'
    },
    {
        name: 'Class',
        entity: 'Class',
        label: 'Class (Cost Center / Segment)',
        category: 'Accounting & GL',
        defaultTable: 'CTLFIL',
        icon: 'category',
        color: 'pink',
        description: 'Class tracking, profit centers, divisions, product lines'
    },
    {
        name: 'Department',
        entity: 'Department',
        label: 'Department (Location / Branch)',
        category: 'Accounting & GL',
        defaultTable: 'CTLFIL',
        icon: 'domain',
        color: 'rose',
        description: 'Department tracking, locations, stores, regional offices'
    }
];

// Map of common aliases/legacy plural forms to canonical target QB entity name
const ENTITY_ALIAS_MAP = {
    'customer': 'Customer',
    'customers': 'Customer',
    'vendor': 'Vendor',
    'vendors': 'Vendor',
    'item': 'Item',
    'items': 'Item',
    'account': 'Account',
    'accounts': 'Account',
    'invoice': 'Invoice',
    'invoices': 'Invoice',
    'order': 'Invoice',
    'orders': 'Invoice',
    'invoiceline': 'Invoice',
    'order lines': 'Invoice',
    'bill': 'Bill',
    'bills': 'Bill',
    'payment': 'Payment',
    'payments': 'Payment',
    'journalentry': 'JournalEntry',
    'journal entries': 'JournalEntry',
    'journal_entry': 'JournalEntry',
    'purchaseorder': 'PurchaseOrder',
    'purchase orders': 'PurchaseOrder',
    'purchase_order': 'PurchaseOrder',
    'class': 'Class',
    'classes': 'Class',
    'department': 'Department',
    'departments': 'Department',
    'location': 'Department',
    'locations': 'Department'
};

/**
 * Normalizes any entity string or entity object to canonical TargetQBEntity
 * @param {string|object} rawName 
 * @returns {string} Canonical name, e.g. "Customer", "Invoice"
 */
export function normalizeEntityName(rawName) {
    if (!rawName) return 'Customer';
    const str = typeof rawName === 'string' ? rawName : (rawName.name || rawName.entity || String(rawName));
    const clean = String(str).trim().toLowerCase();
    return ENTITY_ALIAS_MAP[clean] || str;
}

/**
 * Retrieves metadata for a target QB entity
 * @param {string|object} entityName 
 * @returns {object} Entity metadata object
 */
export function getQbEntityMeta(entityName) {
    const canonical = normalizeEntityName(entityName);
    const found = QUICKBOOKS_TARGET_ENTITIES.find(e => (e.entity || e.name).toLowerCase() === String(canonical).toLowerCase());
    return found || {
        name: canonical,
        entity: canonical,
        label: canonical,
        category: 'General',
        defaultTable: 'CUSFIL',
        icon: 'table_chart',
        color: 'slate',
        description: `${canonical} records in QuickBooks Online`
    };
}
